<!-- resources/js/components/campaigns.vue -->
<template>
    <div class="campaigns">
        <CampaignsHeader :is-admin="isAdmin" :syncing="syncing" :total-count="filteredCampaigns.length"
            :status-dict="statusDict" :advertisers="advertisers" :brands="brands"
            :selected-advertiser-ids="selectedAdvertiserIds" :selected-brand-ids="selectedBrandIds"
            @update:query="query = $event" @update:status-filter="statusFilter = $event"
            @update:selected-advertiser-ids="selectedAdvertiserIds = $event"
            @update:selected-brand-ids="selectedBrandIds = $event"
            @update:sort-by="sortBy = $event" @update:sort-direction="sortDirection = $event" />

        <div class="campaigns__list">
            <!-- Skeleton loader during sync -->
            <CampaignsSkeleton v-if="syncing" :campaigns="campaignsSafe" />

            <!-- Empty state: no campaigns or no results after filters -->
            <div v-else-if="filteredCampaigns.length === 0" class="campaigns__empty">
                <p class="campaigns__empty-text">
                    {{ campaignsSafe.length === 0 ? 'Кампаний пока нет. Создайте первую кампанию.' : 'Нет кампаний по заданным фильтрам. Измените параметры поиска или фильтры.' }}
                </p>
            </div>

            <!-- Actual campaigns -->
            <CampaignCard v-else v-for="(c, idx) in filteredCampaigns" :key="rowKey(c, idx)" :campaign="c"
                :is-expanded="isOpen(rowKey(c, idx))" :ads="adsOf(c.id)" :ads-loading="isAdsLoading(c.id)"
                :ads-error="adsError(c.id)" :is-cancelling-id="isCancellingId" :is-ad-cancelling-id="isAdCancellingId"
                :is-changing-status="isChangingStatus" :can-delete-campaigns="canDeleteCampaigns"
                :normalize-status="normalizeStatus" :status-label="statusLabel" :format-period="formatPeriod"
                :format-updated="formatUpdated" :day-name="dayName" :ms-to-h-m="msToHM" :can-create-ad="canCreateAd"
                :can-cancel-ad="canCancelAd" :can-change-status="canChangeStatus" :fr-count="frCount"
                @add-ad="goToCreateAd" @toggle-expand="toggle(rowKey(c, idx))" @cancel-campaign="confirmCancel"
                @cancel-ad="(ad) => confirmAdCancel(c.id, ad)" @change-status="showStatusChangeModal" />
        </div>
    </div>

    <CampaignModals :show-campaign-confirm="showConfirm" :show-ad-confirm="adCancelModal" @cancel-modal="cancelModal"
        @confirm-cancel="doCancel" @cancel-ad-modal="cancelAdModal" @confirm-ad-cancel="doAdCancel" />

    <StatusChangeModal :show="statusChangeModal" :campaign="campaignToChangeStatus"
        :current-status="campaignToChangeStatus?.status" :is-admin="isAdmin" @close="closeStatusChangeModal"
        @status-changed="handleStatusChanged" />
</template>

<script>
import CampaignAds from './CampaignAds.vue';
import FrameCard from '../create/FrameCard.vue';
import FramesSection from '../create/FramesSection.vue';
import CampaignCard from './CampaignCard.vue';
import CampaignsHeader from './CampaignsHeader.vue';
import CampaignsSkeleton from './CampaignsSkeleton.vue';
import CampaignModals from '../create/CampaignModals.vue';
import StatusChangeModal from './StatusChangeModal.vue';
import { mapCampaignStatus, getCampaignStatusLabel } from '../../../utils/campaignStatus.js';
import { formatInRtbTZ, formatDateOnlyInRtbTZ } from '../../../utils/timezones';
export default {
    name: 'Campaigns',
    components: { CampaignAds, FrameCard, FramesSection, CampaignCard, CampaignsHeader, CampaignsSkeleton, CampaignModals, StatusChangeModal },
    props: {
        campaigns: {
            type: Array,
            required: true,
            validator: (value) => Array.isArray(value)
        },
        creatives: {
            type: Array,
            required: true,
            validator: (value) => Array.isArray(value)
        },
        advertisers: {
            type: Array,
            default: () => []
        },
        brands: {
            type: Array,
            default: () => []
        },
        user: {
            type: Object,
            required: true,
            validator: (value) => value && typeof value === 'object'
        }
    },
    data() {
        return {
            openMap: {},
            query: '',
            statusFilter: '',
            selectedAdvertiserIds: [],
            selectedBrandIds: [],
            sortBy: 'id',
            sortDirection: 'desc',
            showConfirm: false,
            campaignToCancel: null,
            isCancellingId: null,
            syncing: false,
            // Ads state
            adsMap: {}, // campaignId -> { list: [], loading: false, error: string|null }
            isAdCancellingId: null,
            adCancelModal: false,
            adToCancel: null,
            adCampaignId: null,
            // Status change state
            statusChangeModal: false,
            campaignToChangeStatus: null,
            isChangingStatus: false,
            days: {
                1: 'Понедельник',
                2: 'Вторник',
                3: 'Среда',
                4: 'Четверг',
                5: 'Пятница',
                6: 'Суббота',
                7: 'Воскресенье',
            },
            statusDict: {
                // Упрощенные статусы для отображения пользователям
                active: 'Active',
                paused: 'Paused',
                completed: 'Completed',
                finished: 'Finished', // Для обратной совместимости
                moderation: 'Moderation',
                // Старые статусы для обратной совместимости (для объявлений)
                approve: 'approve',
                reserved: 'reserved',
                booked: 'booked',
                cancel: 'cancel',
                review: 'review',
                reserve: 'reserve',
                activated: 'activated',
                rejected: 'rejected',
                cancelled: 'cancelled',
                pending_approval: 'pending_approval',
            },
        };
    },
    computed: {
        campaignsSafe() {
            return Array.isArray(this.campaigns) ? this.campaigns : [];
        },
        filteredCampaigns() {
            const q = (this.query || '').toLowerCase();
            const status = (this.statusFilter || '').toLowerCase();
            const advIds = Array.isArray(this.selectedAdvertiserIds) ? this.selectedAdvertiserIds.map((id) => Number(id)) : [];
            const brandIds = Array.isArray(this.selectedBrandIds) ? this.selectedBrandIds.map((id) => Number(id)) : [];
            const hasAdvertiserBrandFilter = advIds.length > 0 || brandIds.length > 0;

            let filtered = this.campaignsSafe.filter((c) => {
                const nameOk = q
                    ? String(c.name || '')
                        .toLowerCase()
                        .includes(q)
                    : true;
                const statusOk = status ? this.mapCampaignStatus(c.status) === status : true;
                const advertiserBrandOk = !hasAdvertiserBrandFilter
                    ? true
                    : (advIds.length > 0 && advIds.indexOf(Number(c.advertiser_id)) !== -1) ||
                      (brandIds.length > 0 && brandIds.indexOf(Number(c.brand_id)) !== -1);
                return nameOk && statusOk && advertiserBrandOk;
            });

            // Применяем сортировку
            const sorted = [...filtered].sort((a, b) => {
                let result = 0;

                switch (this.sortBy) {
                    case 'name':
                        const nameA = String(a.name || '').toLowerCase();
                        const nameB = String(b.name || '').toLowerCase();
                        result = nameA.localeCompare(nameB, 'ru');
                        break;

                    case 'id':
                        // Сортировка по дате создания
                        result = (a.id || 0) - (b.id || 0);
                        break;

                    case 'updated_ms':
                        // Сортировка по дате обновления
                        result = (a.updated_ms || 0) - (b.updated_ms || 0);
                        break;

                    case 'period_start':
                        // Сортировка по дате начала кампании
                        const startA = this.parseDate(a.period_start);
                        const startB = this.parseDate(b.period_start);
                        if (!startA && !startB) result = 0;
                        else if (!startA) result = 1;
                        else if (!startB) result = -1;
                        else result = startA.getTime() - startB.getTime();
                        break;

                    case 'period_end':
                        // Сортировка по дате окончания кампании
                        const endA = this.parseDate(a.period_end);
                        const endB = this.parseDate(b.period_end);
                        if (!endA && !endB) result = 0;
                        else if (!endA) result = 1;
                        else if (!endB) result = -1;
                        else result = endA.getTime() - endB.getTime();
                        break;

                    default:
                        result = 0;
                }

                // Применяем направление сортировки
                return this.sortDirection === 'asc' ? result : -result;
            });

            return sorted;
        },
        isAdmin() {
            return this.user?.is_admin === true;
        },
        canManageCampaigns() {
            return this.user?.permissions?.can_manage_campaigns === true;
        },
        canDeleteCampaigns() {
            return this.user?.permissions?.can_delete_campaigns === true;
        },
    },
    methods: {
        rowKey(c, idx) {
            return String(c.external_id || c.id || `row-${idx}`);
        },
        toggle(key) {
            this.openMap[key] = !this.openMap[key];
            // lazy-load ads on open
            const camp = (this.campaignsSafe || []).find((c, idx) => this.rowKey(c, idx) === key);
            if (this.openMap[key] && camp) {
                this.ensureAdsLoaded(camp.id);
            }
        },
        isOpen(key) {
            return !!this.openMap[key];
        },
        frCount(c) {
            return Array.isArray(c.frames) ? c.frames.length : 0;
        },
        // Ads helpers - теперь объявления загружаются вместе с кампаниями из БД
        ensureAdsLoaded(campaignId) {
            // Объявления теперь уже загружены вместе с кампаниями
            // Этот метод больше не делает API вызовов
            if (!campaignId) return;
            if (!this.adsMap[campaignId]) {
                this.adsMap = {
                    ...this.adsMap,
                    [campaignId]: {
                        list: [],
                        loading: false,
                        error: null,
                    },
                };
            }
        },
        adsOf(campaignId) {
            // Теперь берем объявления из данных кампании, а не из adsMap
            const campaign = this.campaignsSafe.find(c => c.id === campaignId);
            return Array.isArray(campaign?.advertisements) ? campaign.advertisements : [];
        },
        adsCount(campaignId) {
            return this.adsOf(campaignId).length;
        },
        isAdsLoading(campaignId) {
            // Объявления теперь загружаются вместе с кампаниями, поэтому загрузки нет
            return false;
        },
        adsError(campaignId) {
            // Объявления теперь загружаются вместе с кампаниями, поэтому ошибок нет
            return null;
        },
        normalizeStatus(s, campaign = null) {
            // Для админов показываем реальный статус как есть
            if (this.isAdmin) {
                if (!s) return 'unknown';
                return String(s).toLowerCase();
            }
            
            // Для не-админов статусы уже преобразованы на бэкенде
            // Просто возвращаем их как есть
            if (campaign && campaign.status) {
                const status = String(campaign.status).toLowerCase();
                // Статусы уже преобразованы на бэкенде: active, paused, completed, moderation
                if (['active', 'paused', 'completed', 'moderation'].includes(status)) {
                    return status;
                }
                // Если по какой-то причине статус не преобразован, применяем маппинг
                return this.mapCampaignStatus(campaign.status);
            }
            
            // Если передан только статус (для объявлений) — маппинг как у кампаний (active/paused/finished/moderation)
            if (!s) return 'moderation';
            return this.mapCampaignStatus(s);
        },
        statusLabel(s, campaign = null) {
            // Для админов показываем реальный статус кампании
            if (this.isAdmin && campaign && campaign.status) {
                return String(campaign.status);
            }
            // Для не-админов кампании — label по отображаемому статусу
            if (campaign && campaign.status) {
                const displayStatus = this.normalizeStatus(s, campaign);
                return this.getCampaignStatusLabel(displayStatus);
            }
            // Объявления: админ — реальный статус, не-админ — label как у кампаний
            if (!campaign) {
                if (this.isAdmin) return String(s ?? '');
                return this.getCampaignStatusLabel(this.mapCampaignStatus(s));
            }
            return this.getCampaignStatusLabel(this.normalizeStatus(s, campaign)) || String(s ?? '');
        },
        mapCampaignStatus(status) {
            return mapCampaignStatus(status);
        },
        getCampaignStatusLabel(displayStatus) {
            return getCampaignStatusLabel(displayStatus);
        },
        formatPeriod(start, end) {
            const s = this.formatDate(start);
            const e = this.formatDate(end);
            if (!s && !e) return '—';
            if (s && e) return `${s} — ${e}`;
            return s || e || '—';
        },
        formatUpdated(ms) {
            const n = Number(ms);
            if (!Number.isFinite(n) || n <= 0) return 'Обновление не указано';
            const d = new Date(n);
            if (isNaN(d.getTime())) return 'Обновление не указано';
            return formatInRtbTZ(d);
        },
        formatDate(value) {
            return formatDateOnlyInRtbTZ(value);
        },
        formatDateTime(dateObj) {
            return formatInRtbTZ(dateObj);
        },
        parseDate(v) {
            if (v instanceof Date) return v;
            if (typeof v === 'number') return new Date(v);
            if (typeof v === 'string') {
                // supports 'YYYY-MM-DD', 'YYYY-MM-DD HH:mm:ss', ISO
                const replaced = v.replace(' ', 'T');
                const d = new Date(replaced);
                if (!isNaN(d.getTime())) return d;
            }
            return null;
        },
        msToHM(ms) {
            const n = Number(ms);
            if (!Number.isFinite(n) || n < 0) return '00:00';
            if (n === 0) return '00:00';
            // Если время равно 24 часам (86400000 мс), показываем "24:00" вместо "00:00"
            if (n === 86400000) {
                return '24:00';
            }
            const totalSec = Math.floor(n / 1000);
            const hh = Math.floor(totalSec / 3600);
            const mm = Math.floor((totalSec % 3600) / 60);
            return `${String(hh).padStart(2, '0')}:${String(mm).padStart(2, '0')}`;
        },
        dayName(n) {
            return this.days[n] || `День ${n}`;
        },
        safeNumber(v) {
            const n = Number(v);
            return Number.isFinite(n) ? n : 0;
        },
        formatMoney(v) {
            const n = Number(v);
            if (!Number.isFinite(n)) return '0';
            return n.toLocaleString('ru-RU', { maximumFractionDigits: 2 });
        },
        prettyId(fr) {
            return String(fr.frame.gid || '—');
        },
        canCancelAd(ad) {
            const k = this.normalizeStatus(ad.status);
            return !['cancel', 'cancelled', 'completed', 'rejected'].includes(k);
        },
        confirmAdCancel(campaignId, ad) {
            if (!this.canCancelAd(ad)) return;
            this.adCampaignId = campaignId;
            this.adToCancel = ad;
            this.adCancelModal = true;
        },
        async doAdCancel() {
            if (!this.adToCancel) return;
            try {
                this.isAdCancellingId = this.adToCancel.id;
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                const res = await fetch(`/api/advertisements/${this.adToCancel.id}/cancel`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken
                    },
                    credentials: 'same-origin',
                });
                if (!res.ok) {
                    const body = await res.text();
                    let errorMessage = 'Не удалось отменить объявление';
                    try {
                        const errorJson = JSON.parse(body);
                        if (errorJson.error) {
                            errorMessage = errorJson.error;
                        }
                    } catch (parseError) {
                        // Если не JSON, используем сообщение по умолчанию
                    }
                    throw new Error(errorMessage);
                }
                // Обновляем статус объявления в данных кампании
                const campaign = this.campaignsSafe.find(c => c.id === this.adCampaignId);
                if (campaign && Array.isArray(campaign.advertisements)) {
                    const adIndex = campaign.advertisements.findIndex(ad => ad.id === this.adToCancel.id);
                    if (adIndex !== -1) {
                        campaign.advertisements[adIndex].status = 'cancelled';
                    }
                }
                this.$notify({
                    title: 'Успех',
                    message: 'Объявление отменено',
                    type: 'success'
                });
            } catch (e) {
                this.$notify({
                    title: 'Ошибка',
                    message: e.message || 'Не удалось отменить объявление',
                    type: 'error'
                });
            } finally {
                this.isAdCancellingId = null;
                this.adCancelModal = false;
                this.adToCancel = null;
                this.adCampaignId = null;
            }
        },
        cancelAdModal() {
            this.adCancelModal = false;
            this.adToCancel = null;
            this.adCampaignId = null;
        },
        canCreateAd(campaign) {
            // Проверяем флаг из бэкенда (статус >= reserve) И наличие креативов
            const canCreate = campaign.can_create_advertisement === true;
            const hasAvailableCreatives = Array.isArray(this.creatives) && this.creatives.length > 0;
            return canCreate && hasAvailableCreatives;
        },
        goToCreateAd(campaign) {
            if (!this.canCreateAd(campaign)) return;
            // Для групп кампаний (campaign_external) переходим на страницу создания объявления для группы
            if (campaign.campaign_external_id) {
                window.location.href = `/guaranteed/campaigns/external/${campaign.campaign_external_id}/advertisements/create`;
            } else {
                window.location.href = `/guaranteed/campaigns/${campaign.id}/advertisements/create`;
            }
        },
        confirmCancel(campaign) {
            this.campaignToCancel = campaign;
            this.showConfirm = true;
        },
        async doCancel() {
            if (!this.campaignToCancel) return;
            try {
                this.isCancellingId = this.campaignToCancel.id;
                const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content');
                const res = await fetch(`/api/campaigns/${this.campaignToCancel.id}/delete`, {
                    method: 'POST',
                    headers: {
                        'Accept': 'application/json',
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': csrfToken
                    },
                    credentials: 'same-origin',
                });
                if (!res.ok) {
                    const data = await res.json().catch(() => ({}));
                    throw new Error(data.error || 'Delete failed');
                }
                // Убираем кампанию из списка (в БД статус deleted, пользователю не показываем)
                this.campaigns = (this.campaigns || []).filter(
                    (c) => c.id !== this.campaignToCancel.id
                );
                this.$notify({
                    title: 'Успех',
                    message: 'Кампания удалена',
                    type: 'success'
                });
            } catch (e) {
                this.$notify({
                    title: 'Ошибка',
                    message: e.message || 'Не удалось удалить кампанию',
                    type: 'error'
                });
            } finally {
                this.isCancellingId = null;
                this.showConfirm = false;
                this.campaignToCancel = null;
            }
        },
        cancelModal() {
            this.showConfirm = false;
            this.campaignToCancel = null;
        },
        async syncStatuses() {
            if (this.syncing) return;
            this.syncing = true;

            try {
                // Шаг 1: Синхронизация статусов с внешним API (кампаний и advertisements)
                const syncResponse = await this.performStatusSync();

                // Шаг 2: Обновление локальных данных
                const updatedCampaigns = await this.fetchUpdatedCampaigns();

                // Шаг 3: Обновление UI
                this.updateLocalCampaigns(updatedCampaigns);

                // Шаг 4: Уведомление пользователя
                this.showSuccessNotification(
                    syncResponse.updated || 0,
                    syncResponse.campaigns_updated || 0,
                    syncResponse.advertisements_updated || 0
                );

            } catch (error) {
                this.handleSyncError(error);
            } finally {
                this.syncing = false;
            }
        },

        // Вспомогательные методы для лучшей читаемости и тестируемости
        async performStatusSync() {
            const csrfToken = document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || '';
            const response = await fetch('/api/campaigns/sync-statuses', {
                method: 'POST',
                headers: {
                    'Accept': 'application/json',
                    'Content-Type': 'application/json',
                    'X-CSRF-TOKEN': csrfToken,
                },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Синхронизация статусов не удалась: ${errorText}`);
            }

            return await response.json();
        },

        async fetchUpdatedCampaigns() {
            const response = await fetch('/api/campaigns/updated', {
                method: 'GET',
                headers: { Accept: 'application/json' },
                credentials: 'same-origin',
            });

            if (!response.ok) {
                throw new Error('Не удалось получить обновленный список кампаний');
            }

            const payload = await response.json();
            return Array.isArray(payload?.list) ? payload.list : [];
        },

        updateLocalCampaigns(updatedCampaigns) {
            // Создаем карту для быстрого поиска
            const campaignsMap = new Map(
                updatedCampaigns
                    .filter(campaign => campaign?.id)
                    .map(campaign => [String(campaign.id), campaign])
            );

            // Обновляем только измененные кампании и их advertisements
            let updatedCount = 0;
            this.campaigns.forEach((campaign) => {
                const campaignId = String(campaign.id);
                const freshCampaign = campaignsMap.get(campaignId);

                if (!freshCampaign) return;

                // Обновляем статус кампании (поддерживаем user_status/system_status для RTB)
                if (freshCampaign.user_status !== undefined) {
                    campaign.user_status = freshCampaign.user_status;
                }
                if (freshCampaign.system_status !== undefined) {
                    campaign.system_status = freshCampaign.system_status;
                }
                if (freshCampaign.status && freshCampaign.status !== campaign.status) {
                    campaign.status = freshCampaign.status;
                    updatedCount++;
                }

                // Обновляем advertisements, если они есть
                if (Array.isArray(freshCampaign.advertisements)) {
                    campaign.advertisements = freshCampaign.advertisements;
                }
            });

            return updatedCount;
        },

        showSuccessNotification(totalUpdated, campaignsUpdated, advertisementsUpdated) {
            const parts = [];

            if (campaignsUpdated > 0) {
                parts.push(`${campaignsUpdated} кампаний`);
            }

            if (advertisementsUpdated > 0) {
                parts.push(`${advertisementsUpdated} объявлений`);
            }

            const message = totalUpdated > 0
                ? `Обновлено: ${parts.join(', ')}`
                : 'Статусы актуальны';

            this.$notify?.({
                title: 'Синхронизация завершена',
                message: message,
                type: 'success',
                duration: 3000,
            });
        },

        handleSyncError(error) {
            this.$notify?.({
                title: 'Ошибка синхронизации',
                message: error.message || 'Не удалось обновить статусы кампаний',
                type: 'error',
                duration: 5000,
            });
        },

        // Status change methods: только active/activated → paused, только в БД
        canChangeStatus(campaign) {
            if (!campaign) return false;
            const userStatus = campaign.user_status ?? campaign.status;
            const s = String(userStatus || '').toLowerCase().trim();
            return s === 'active' || s === 'activated';
        },

        showStatusChangeModal(campaign) {
            if (!this.canChangeStatus(campaign)) return;
            this.campaignToChangeStatus = campaign;
            this.statusChangeModal = true;
        },

        closeStatusChangeModal() {
            this.statusChangeModal = false;
            this.campaignToChangeStatus = null;
            this.isChangingStatus = false;
        },

        handleStatusChanged(data) {
            // Обновляем статус кампании в локальном массиве
            const campaignIndex = this.campaigns.findIndex(c => c.id === data.campaignId);
            if (campaignIndex !== -1) {
                // support updates that may include user/system status
                if (data.user_status !== undefined) this.campaigns[campaignIndex].user_status = data.user_status;
                if (data.system_status !== undefined) this.campaigns[campaignIndex].system_status = data.system_status;
                this.campaigns[campaignIndex].status = data.newStatus ?? this.campaigns[campaignIndex].status;
                this.campaigns[campaignIndex].comment = data.newComment;
                this.campaigns[campaignIndex].updated_ms = Date.now();
            }

            this.closeStatusChangeModal();
        },
    },
};
</script>